import * as XLSX from 'xlsx';

interface MercerRecord {
  rowNumber: string;
  sellerFirstName: string;
  sellerLastName: string;
  buyerFirstName: string;
  buyerLastName: string;
  date: string;
  documentType: string;
  documentNumber: string;
  legalDescription: string;
}

const parseNames = (rawNames: string): { firstName: string; lastName: string } => {
  if (!rawNames || rawNames.trim() === '') {
    return { firstName: '', lastName: '' };
  }

  // Take first name only if multiple names separated by semicolons
  const names = rawNames.split(';')[0].trim();
  
  // Remove suffixes and roles
  const cleanName = names
    .replace(/\s+TRUSTEE$/i, '')
    .replace(/\s+TR$/i, '')
    .replace(/\s+LLC$/i, '')
    .replace(/\s+INC$/i, '')
    .replace(/\s+TRUST$/i, '')
    .replace(/\s+JR$/i, '')
    .replace(/\s+SR$/i, '')
    .replace(/\s+II$/i, '')
    .replace(/\s+III$/i, '')
    .replace(/\s+IV$/i, '')
    .trim();
  
  // Handle LLC/corporate names
  if (cleanName.includes(' LLC') || cleanName.includes(' INC') || 
      cleanName.includes(' PROPERTIES') || cleanName.includes(' FARMS') ||
      cleanName.includes(' ENTERPRISES')) {
    return { firstName: cleanName, lastName: '' };
  }
  
  const parts = cleanName.split(/\s+/);
  
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  
  // Filter out middle initials (single letters)
  const filteredParts = parts.filter(part => part.length > 1 || parts.length === 2);
  
  if (filteredParts.length === 1) {
    return { firstName: filteredParts[0], lastName: '' };
  }
  
  // Format: LASTNAME FIRSTNAME
  const lastName = filteredParts[0];
  const firstName = filteredParts.slice(1).join(' ');
  
  return { firstName, lastName };
};

export const processMercerCountyData = (rawData: string): MercerRecord[] => {
  const lines = rawData.trim().split('\n').filter(line => line.trim() !== '');
  const records: MercerRecord[] = [];
  
  for (const line of lines) {
    const parts = line.split('\t');
    
    if (parts.length < 9) continue;
    
    const rowNumber = parts[0]?.trim() || '';
    const sellerName = parts[3]?.trim() || '';
    const buyerName = parts[4]?.trim() || '';
    const date = parts[5]?.trim() || '';
    const documentType = parts[6]?.trim() || '';
    const documentNumber = parts[8]?.trim() || '';
    const legalDescription = parts[9]?.trim() || '';
    
    const seller = parseNames(sellerName);
    const buyer = parseNames(buyerName);
    
    records.push({
      rowNumber,
      sellerFirstName: seller.firstName,
      sellerLastName: seller.lastName,
      buyerFirstName: buyer.firstName,
      buyerLastName: buyer.lastName,
      date,
      documentType,
      documentNumber,
      legalDescription
    });
  }
  
  return records;
};

export const exportMercerToExcel = (records: MercerRecord[], filename: string = 'mercer_county_records.xlsx'): void => {
  const worksheet = XLSX.utils.json_to_sheet(records.map(r => ({
    'Row': r.rowNumber,
    'Seller First Name': r.sellerFirstName,
    'Seller Last Name': r.sellerLastName,
    'Buyer First Name': r.buyerFirstName,
    'Buyer Last Name': r.buyerLastName,
    'Date': r.date,
    'Document Type': r.documentType,
    'Document Number': r.documentNumber,
    'Legal Description': r.legalDescription
  })));
  
  worksheet['!cols'] = [
    { wch: 6 },   // Row
    { wch: 25 },  // Seller First Name
    { wch: 20 },  // Seller Last Name
    { wch: 25 },  // Buyer First Name
    { wch: 20 },  // Buyer Last Name
    { wch: 12 },  // Date
    { wch: 15 },  // Document Type
    { wch: 18 },  // Document Number
    { wch: 50 }   // Legal Description
  ];
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Mercer County Records');
  
  XLSX.writeFile(workbook, filename);
};
